import React, { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import PINGS from "@/landing/data/socialProofPings";

/**
 * SocialProofBand
 * Marketing-grade auto-scrolling "live activity" ticker on the landing page.
 *
 * Design:
 *   - Fixed 200px left column with a pulsing green dot + "LIVE ACROSS Nexus One"
 *   - Fluid right column with marquee-scrolling ping cards
 *   - Marquee: ~55s per full loop, pauses on hover
 *   - Honors prefers-reduced-motion → static 4-card grid instead
 *   - Disclosure line beneath: "Illustrative activity — actual customer names withheld for privacy."
 *
 * Props:
 *   workers: [{slug, avatar_url, name, ...}] — used to attach the correct portrait to each ping.
 */

function resolveAv(url) {
    if (!url) return "https://images.unsplash.com/photo-1500648767791-00dcc994a43e";
    if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
}

function renderBody(body) {
    // Minimal **bold** parser — no full markdown; body strings are ours only.
    const parts = body.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => {
        if (p.startsWith("**") && p.endsWith("**")) {
            return <b key={i} className="font-semibold text-gray-900">{p.slice(2, -2)}</b>;
        }
        return <span key={i}>{p}</span>;
    });
}

function PingCard({ ping, worker }) {
    const name = worker?.name || ping.slug.split("-")[0];
    return (
        <div
            data-testid={`social-proof-ping-${ping.slug}-${ping.ts.replace(/\s+/g, "-")}`}
            className="inline-flex items-center gap-3 wfos-card px-4 py-2.5 min-w-[340px] max-w-[340px] mr-3 h-14"
        >
            <img
                src={resolveAv(worker?.avatar_url)}
                alt={name}
                className="w-8 h-8 rounded-full object-cover border border-[#e8e8e3] flex-shrink-0"
                loading="lazy"
            />
            <div className="min-w-0 flex-1">
                <div className="text-[12.5px] text-gray-700 leading-snug line-clamp-2">
                    {renderBody(ping.body)}
                </div>
            </div>
            <span className="text-[10px] font-medium text-gray-400 whitespace-nowrap flex-shrink-0">
                {ping.ts}
            </span>
        </div>
    );
}

export default function SocialProofBand({ workers = [] }) {
    const reduce = useReducedMotion();
    const workerBySlug = useMemo(() => {
        const m = {};
        for (const w of workers) m[w.slug] = w;
        return m;
    }, [workers]);

    // Duplicate ping list for a seamless marquee loop
    const track = useMemo(() => [...PINGS, ...PINGS], []);

    if (reduce) {
        // Static 4-card grid fallback
        return (
            <section
                className="border-y border-[#e8e8e3]"
                style={{ backgroundColor: "#f7f8f5" }}
                data-testid="social-proof-band"
            >
                <div className="max-w-6xl mx-auto px-6 py-6">
                    <div className="flex items-center gap-2 mb-4">
                        <LiveDot />
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                            Live across Nexus One
                        </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {PINGS.slice(0, 4).map((p, i) => (
                            <PingCard key={i} ping={p} worker={workerBySlug[p.slug]} />
                        ))}
                    </div>
                    <Disclosure />
                </div>
            </section>
        );
    }

    return (
        <section
            className="border-y border-[#e8e8e3]"
            style={{ backgroundColor: "#f7f8f5" }}
            data-testid="social-proof-band"
        >
            <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-5">
                {/* Left: live label */}
                <div className="flex items-center gap-2 md:w-[200px] flex-shrink-0">
                    <LiveDot />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-gray-500">
                        Live across Nexus One
                    </span>
                </div>

                {/* Right: marquee */}
                <div
                    className="relative overflow-hidden flex-1 w-full group"
                    data-testid="social-proof-marquee"
                    style={{
                        maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                        WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
                    }}
                >
                    <motion.div
                        className="flex whitespace-nowrap"
                        animate={{ x: ["0%", "-50%"] }}
                        transition={{ duration: 55, ease: "linear", repeat: Infinity }}
                        style={{ willChange: "transform" }}
                        onMouseEnter={(e) => (e.currentTarget.style.animationPlayState = "paused")}
                    >
                        {track.map((p, i) => (
                            <PingCard key={i} ping={p} worker={workerBySlug[p.slug]} />
                        ))}
                    </motion.div>
                    {/* Pause-on-hover overlay (CSS-only trick) */}
                    <style>{`
                        [data-testid="social-proof-marquee"]:hover > div { animation-play-state: paused; }
                    `}</style>
                </div>
            </div>
            <Disclosure />
        </section>
    );
}

function LiveDot() {
    return (
        <span className="relative inline-flex w-2 h-2" aria-hidden="true">
            <span className="absolute inline-flex w-full h-full rounded-full bg-[#16a34a] opacity-75 animate-ping" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-[#16a34a]" />
        </span>
    );
}

function Disclosure() {
    return (
        <div className="max-w-6xl mx-auto px-6 pb-3">
            <div
                data-testid="social-proof-disclosure"
                className="text-[10.5px] text-gray-400"
            >
                Illustrative activity — actual customer names withheld for privacy.
            </div>
        </div>
    );
}
